import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import styles from './SelectInput.module.css';
import { issueMessage } from './issueMessage';

/**
 * One option for a `SelectInput` (or `MultiSelectInput`). The `value`
 * is the catalog enum value the engine stores; the `label` is the
 * human-readable text rendered in the dropdown.
 */
export interface ISelectOption {
  value: string;
  label: string;
}

export interface SelectInputProps {
  field: FieldDefinition;
  /**
   * Option list. **The catalog does not currently expose a generic
   * `options` declaration on `FieldDefinition`** — `widgetValueProjection`
   * is PDF-only per ADR 0006 and must not leak into the UI. The caller
   * (`apps/applicant-web` / Phase 3 screener) supplies the option list
   * for now; a future Phase 0.5+ slice may add `options` to the
   * catalog and let `SelectInput` read them directly.
   */
  options: readonly ISelectOption[];
  value: string;
  issues: readonly ValidationIssue[];
  onChange: (next: string) => void;
  /**
   * Whether the user has interacted with the field. When `false`, the
   * primitive suppresses validation issues so the form doesn't show
   * red errors before the user has selected an option. Defaults to
   * `true` for backward compatibility — the applicant-web shell flips
   * it on first interaction or on a deliberate submit.
   */
  touched?: boolean;
  /** Fires on the select's native `onBlur`. */
  onBlur?: () => void;
}

/**
 * Catalog-driven select primitive. Renders a native `<select>` with
 * an empty placeholder option, the supplied option list, the field's
 * label / required-mark / help-text, and validation issues underneath.
 * Wires `aria-invalid` and `aria-describedby` when issues are present.
 */
export function SelectInput({
  field,
  options,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: SelectInputProps): JSX.Element {
  const inputId = `field-${field.id.replace(/\./g, '-')}`;
  const errorListId = `${inputId}-errors`;
  const showIssues = touched && issues.length > 0;
  const selectClassName = showIssues
    ? `${styles.field__select} ${styles['field__select--invalid']}`
    : styles.field__select;

  return (
    <div className={styles.field} data-field-id={field.id}>
      <label htmlFor={inputId} className={styles.field__label}>
        {field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </label>

      <select
        id={inputId}
        value={value}
        onChange={event => onChange(event.target.value)}
        onBlur={onBlur}
        aria-required={field.required === true || undefined}
        aria-invalid={showIssues || undefined}
        aria-describedby={showIssues ? errorListId : undefined}
        className={selectClassName}
      >
        <option value="">— Select —</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {field.helpText !== undefined && field.helpText !== '' ? (
        <p className={styles['field__help-text']}>{field.helpText}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          <ul id={errorListId} className={styles['field__error-list']}>
          {issues.map((issue, index) => (
            <li key={`${issue.code}-${index}`}>
              <span aria-hidden="true" className={styles['field__error-icon']}>
                !
              </span>
              {issueMessage(issue)}
            </li>
          ))}
        </ul>
        </div>
      ) : null}
    </div>
  );
}
